import React, { useMemo, useState } from 'react';
import { X, Settings, Plus, Search, Loader2, RefreshCw } from 'lucide-react';
import {
  CATEGORY_FILTERS,
  TRIAL_STATUS_FILTER_OPTIONS,
  TRIAL_TRACKER_EVENT_TYPES,
  TYPE_META,
  TrialTrackerEventType,
  TrialTrackerIndication,
  TrialTrackerSearchMode,
  isMultiFilterSelected,
} from '../../utils/trialTrackerTimeline';
import IndicationCombo from './IndicationCombo';

export interface SavedView {
  id: number;
  name: string;
  cat_filter: string;
  phase_filter: string;
  source_filter: string;
  followed_snapshot: { follow_type: string; follow_value: string }[];
  alerts_off_snapshot: Record<string, boolean>;
}

export interface FollowRow {
  id: number;
  follow_type: string;
  follow_value: string;
}

interface Props {
  savedViews: SavedView[];
  activeWatchlistId?: number | null;
  onSaveCurrentView: (name: string) => void;
  onLoadView: (view: SavedView) => void;
  onDeleteView: (id: number) => void;

  follows: FollowRow[];
  followSuggestions: { type: 'asset' | 'indication'; value: string }[];
  onAddFollow: (type: 'asset' | 'indication', value: string) => void;
  onRemoveFollow: (id: number) => void;
  onSearchFollow: (type: 'asset' | 'indication', value: string) => void;

  catFilter: string[];
  onToggleCatFilter: (cat: string) => void;

  phaseOptions: string[];
  phaseFilter: string[];
  onTogglePhaseFilter: (phase: string) => void;

  statusFilter: string[];
  onToggleStatusFilter: (status: string) => void;

  sourceOptions: string[];
  sourceFilter: string[];
  onToggleSourceFilter: (source: string) => void;

  alertTypesOff: Record<string, boolean>;
  deliveryMode: 'Instant' | 'Daily' | 'Weekly';
  onUpdateSettings: (alertTypesOff: Record<string, boolean>, deliveryMode: 'Instant' | 'Daily' | 'Weekly') => void;

  indications: TrialTrackerIndication[];
  indicationFilter: string[];
  onSetIndicationFilter: (values: string[]) => void;

  searchQuery: string;
  searchMode: TrialTrackerSearchMode;
  onSearchQueryChange: (q: string) => void;
  onSearchSubmit: () => void;
  onSearchModeChange: (mode: TrialTrackerSearchMode) => void;
  searchLoading: boolean;
  searchError: string | null;
  searchSummary?: string | null;
  onFollowSearchTerm: () => void;

  isSuperuser: boolean;
  selectedIndicationMeta: TrialTrackerIndication | null;
  onRefreshNow: () => void;
  onRefreshIndication?: () => void;
  onFetchForIndication?: () => void;
  refreshing: boolean;
  refreshResult: { checked: number; events_created: number; error?: string } | null;
}

const chipBase = 'text-xs font-medium px-2.5 py-1 rounded-full border cursor-pointer whitespace-nowrap transition-colors';

const TrackerSidebar: React.FC<Props> = ({
  savedViews, activeWatchlistId, onSaveCurrentView, onLoadView, onDeleteView,
  follows, followSuggestions, onAddFollow, onRemoveFollow, onSearchFollow,
  catFilter, onToggleCatFilter,
  phaseOptions, phaseFilter, onTogglePhaseFilter,
  statusFilter, onToggleStatusFilter,
  sourceOptions, sourceFilter, onToggleSourceFilter,
  alertTypesOff, deliveryMode, onUpdateSettings,
  indications, indicationFilter, onSetIndicationFilter,
  searchQuery, searchMode, onSearchQueryChange, onSearchSubmit, onSearchModeChange,
  searchLoading, searchError, searchSummary, onFollowSearchTerm,
  isSuperuser, selectedIndicationMeta, onRefreshNow, onRefreshIndication, onFetchForIndication, refreshing, refreshResult,
}) => {
  const [savingView, setSavingView] = useState(false);
  const [viewName, setViewName] = useState('');
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderQuery, setBuilderQuery] = useState('');
  const [alertPopoverOpen, setAlertPopoverOpen] = useState(false);

  const filteredSuggestions = useMemo(() => {
    const q = builderQuery.trim().toLowerCase();
    // Unfiltered, this list is assets-then-indications sorted alphabetically — a low cap here
    // would get entirely eaten by names starting with "A" before indications ever appear.
    // Scroll (below) handles the length instead of truncating.
    if (!q) return followSuggestions.slice(0, 500);
    return followSuggestions.filter((s) => s.value.toLowerCase().includes(q)).slice(0, 500);
  }, [followSuggestions, builderQuery]);

  const mutedCount = TRIAL_TRACKER_EVENT_TYPES.filter((t) => alertTypesOff[t]).length;

  const searchModes: { key: TrialTrackerSearchMode; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'asset', label: 'Asset' },
    { key: 'indication', label: 'Indication' },
    { key: 'nct', label: 'NCT' },
  ];

  const canFollowSearchTerm = searchQuery.trim().length >= 2
    && (searchMode === 'asset' || searchMode === 'indication' || searchMode === 'all');
  const followTypeForSearch: 'asset' | 'indication' = searchMode === 'indication' ? 'indication' : 'asset';
  const alreadyFollowingSearch = follows.some(
    (f) => f.follow_type === followTypeForSearch && f.follow_value.toLowerCase() === searchQuery.trim().toLowerCase(),
  );

  return (
    <div className="w-72 shrink-0 min-h-0 self-stretch bg-tracker-sidebar border-r border-tracker-line flex flex-col overflow-hidden">
      <div className="flex-1 min-w-0 min-h-0 overflow-y-auto p-3.5">
        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5">SEARCH TRIALS</div>
        <div className="flex flex-wrap gap-1 mb-2">
          {searchModes.map((m) => (
            <span
              key={m.key}
              onClick={() => onSearchModeChange(m.key)}
              className={`${chipBase} ${searchMode === m.key ? 'bg-tracker-accent border-tracker-accent text-white font-semibold' : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
            >
              {m.label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1.5 bg-white border border-tracker-line rounded-md px-2 py-1.5 mb-1.5 focus-within:border-tracker-accent">
          <Search size={12} className="text-tracker-muted flex-none" />
          <input
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearchSubmit();
              }
            }}
            placeholder={
              searchMode === 'nct' ? 'NCT ID…'
                : searchMode === 'asset' ? 'Asset / intervention…'
                  : searchMode === 'indication' ? 'Indication / condition…'
                    : 'Trials, assets, indications, NCT…'
            }
            className="flex-1 min-w-0 text-[11.5px] text-tracker-ink outline-none"
          />
          {searchLoading && <Loader2 size={12} className="text-tracker-muted animate-spin flex-none" />}
          {searchQuery && !searchLoading && (
            <X size={12} className="text-tracker-muted cursor-pointer hover:text-tracker-ink flex-none" onClick={() => onSearchQueryChange('')} />
          )}
        </div>
        <button
          type="button"
          onClick={onSearchSubmit}
          disabled={searchQuery.trim().length < 2 || searchLoading}
          className="w-full mb-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-tracker-accent text-white hover:bg-tracker-accentDark disabled:opacity-40 inline-flex items-center justify-center gap-1.5 transition-all"
        >
          {searchLoading ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Searching…
            </>
          ) : (
            'Search'
          )}
        </button>
        <div className="text-[10px] text-tracker-muted mb-2 leading-snug">
          Press Enter or click Search to run. Searching as you type can time out on ClinicalTrials.gov.
          Results include our database plus interventional CT.gov trials (up to 100).
        </div>
        {searchError && <div className="text-[10.5px] text-rose-600 mb-2">{searchError}</div>}
        {canFollowSearchTerm && (
          <button
            type="button"
            onClick={onFollowSearchTerm}
            disabled={alreadyFollowingSearch}
            className="w-full mb-2 text-[11px] font-semibold px-3 py-1.5 rounded-md border border-tracker-line text-tracker-accent hover:border-tracker-cardBorderUnread disabled:opacity-40"
          >
            {alreadyFollowingSearch ? 'Already following' : `Follow ${followTypeForSearch}: "${searchQuery.trim()}"`}
          </button>
        )}
        {searchSummary && searchQuery.trim().length >= 2 && (
          <div className="text-[10px] text-tracker-muted mb-3.5 leading-snug">
            {searchSummary}. Results appear in the main panel.
          </div>
        )}
        {!searchSummary && <div className="mb-3.5" />}

        {/* Watchlists / saved views */}
        <div className="flex items-center mb-2 px-0.5">
          <span className="text-[10.5px] font-bold tracking-wide text-tracker-muted">WATCHLISTS</span>
          <button
            onClick={() => setSavingView((v) => !v)}
            className="ml-auto text-[10.5px] font-semibold px-2.5 py-0.5 rounded-full border border-dashed border-tracker-muted text-tracker-muted hover:border-tracker-accent hover:text-tracker-accent"
          >
            {savingView ? 'Cancel' : 'Save current'}
          </button>
        </div>
        {savingView && (
          <div className="bg-white border border-tracker-line rounded-lg p-2.5 mb-3 shadow-sm">
            <input
              autoFocus
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && viewName.trim()) {
                  onSaveCurrentView(viewName.trim());
                  setViewName('');
                  setSavingView(false);
                }
              }}
              placeholder="Name this view…"
              className="w-full text-[11.5px] text-tracker-ink px-2 py-1.5 border border-tracker-line rounded-md outline-none focus:border-tracker-accent mb-2"
            />
            <button
              onClick={() => {
                if (!viewName.trim()) return;
                onSaveCurrentView(viewName.trim());
                setViewName('');
                setSavingView(false);
              }}
              className="text-[11px] font-semibold px-3 py-1 rounded-full bg-tracker-accent text-white hover:bg-tracker-accentDark"
            >
              Save view
            </button>
          </div>
        )}
        <div className="flex flex-col gap-1.5 mb-3.5 max-h-40 overflow-y-auto pr-1">
          {savedViews.length === 0 && (
            <div className="text-[10.5px] text-tracker-muted leading-snug px-0.5">
              No saved views yet — set your filters, then "Save current".
            </div>
          )}
          {savedViews.map((v) => (
            <div
              key={v.id}
              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer ${
                activeWatchlistId === v.id
                  ? 'bg-tracker-cardBgSelected border-tracker-cardBorderSelected'
                  : 'bg-white border-tracker-line hover:border-tracker-cardBorderUnread'
              }`}
              onClick={() => onLoadView(v)}
            >
              <span className="w-1.5 h-1.5 rounded-sm bg-tracker-accent flex-none" />
              <span className="flex-1 min-w-0 text-[12px] font-semibold text-tracker-ink truncate">{v.name}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteView(v.id); }}
                className="opacity-0 group-hover:opacity-100 text-tracker-muted hover:text-tracker-ink text-sm leading-none"
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Following */}
        <div className="flex items-center gap-1.5 mb-2 px-0.5">
          <span className="text-[10.5px] font-bold tracking-wide text-tracker-muted">FOLLOWING</span>
          <button
            onClick={() => setBuilderOpen((v) => !v)}
            className="ml-auto text-[10.5px] font-semibold px-2.5 py-0.5 rounded-full border border-dashed border-tracker-muted text-tracker-muted hover:border-tracker-accent hover:text-tracker-accent flex items-center gap-1"
          >
            <Plus size={11} />
            {builderOpen ? 'Close' : 'Follow'}
          </button>
        </div>
        {builderOpen && (
          <div className="bg-white border border-tracker-line rounded-lg p-2.5 mb-3 shadow-sm">
            <input
              autoFocus
              value={builderQuery}
              onChange={(e) => setBuilderQuery(e.target.value)}
              placeholder="Quick search assets, indications…"
              className="w-full text-[11.5px] text-tracker-ink bg-tracker-sidebar border border-tracker-line rounded-md px-2.5 py-1.5 mb-2 outline-none focus:border-tracker-accent"
            />
            <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto pr-1">
              {filteredSuggestions.map((s) => {
                const already = follows.some((f) => f.follow_type === s.type && f.follow_value === s.value);
                return (
                  <span
                    key={`${s.type}:${s.value}`}
                    onClick={() => !already && onAddFollow(s.type, s.value)}
                    className={`${chipBase} ${already ? 'bg-tracker-statusBg border-tracker-cardBorderUnread text-tracker-accent' : 'bg-tracker-sidebar border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
                  >
                    {s.value}
                  </span>
                );
              })}
              {filteredSuggestions.length === 0 && (
                <span className="text-[10.5px] text-tracker-muted">No catalog matches</span>
              )}
              {builderQuery.trim().length >= 2 && (
                <div className="w-full flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t border-tracker-line">
                  {(['asset', 'indication'] as const).map((t) => {
                    const val = builderQuery.trim();
                    const already = follows.some(
                      (f) => f.follow_type === t && f.follow_value.toLowerCase() === val.toLowerCase(),
                    );
                    return (
                      <button
                        key={`custom-${t}`}
                        type="button"
                        disabled={already}
                        onClick={() => {
                          if (!already) onAddFollow(t, val);
                        }}
                        className={`${chipBase} ${already ? 'opacity-40' : 'bg-white border-dashed border-tracker-accent text-tracker-accent hover:bg-tracker-statusBg'}`}
                      >
                        Follow {t}: “{val}”
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {follows.map((f) => (
            <span
              key={f.id}
              role="button"
              tabIndex={0}
              title={`Search ${f.follow_type}: ${f.follow_value}`}
              onClick={() => onSearchFollow(f.follow_type as 'asset' | 'indication', f.follow_value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSearchFollow(f.follow_type as 'asset' | 'indication', f.follow_value);
                }
              }}
              className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-tracker-statusBg text-tracker-accent cursor-pointer hover:border hover:border-tracker-cardBorderUnread border border-transparent"
            >
              {f.follow_value}
              <X
                size={11}
                className="opacity-60 cursor-pointer hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFollow(f.id);
                }}
              />
            </span>
          ))}
          {follows.length === 0 && <span className="text-[10.5px] text-tracker-muted px-0.5">Not following anything yet.</span>}
        </div>

        {/* Category filters */}
        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5 mt-1">STATUS UPDATES</div>
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {CATEGORY_FILTERS.map((c) => (
            <span
              key={c.key}
              onClick={() => onToggleCatFilter(c.key)}
              className={`${chipBase} ${isMultiFilterSelected(catFilter, c.key) ? 'bg-tracker-accent border-tracker-accent text-white font-semibold' : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
            >
              {c.label}
            </span>
          ))}
        </div>

        {/* Indication filter — separates trials by canonical indication rather than free-text */}
        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5">INDICATION</div>
        <IndicationCombo
          multi
          indications={indications}
          values={indicationFilter}
          onChangeMulti={onSetIndicationFilter}
        />

        {selectedIndicationMeta && (
          <div className="-mt-2 mb-3.5 px-0.5 flex items-center justify-between gap-2">
            <span className="text-[10.5px] text-tracker-muted">
              {selectedIndicationMeta.trial_count} trial{selectedIndicationMeta.trial_count === 1 ? '' : 's'}
            </span>
            {isSuperuser && (
              selectedIndicationMeta.trial_count > 0 ? (
                <button
                  onClick={onRefreshIndication}
                  disabled={refreshing}
                  title="Re-check this indication's trials against ClinicalTrials.gov now"
                  className="flex-none text-tracker-muted hover:text-tracker-accent disabled:opacity-40"
                >
                  <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                </button>
              ) : (
                <button
                  onClick={onFetchForIndication}
                  className="flex-none text-[10.5px] font-semibold text-tracker-accent hover:text-tracker-accentDark whitespace-nowrap"
                >
                  Fetch trials →
                </button>
              )
            )}
          </div>
        )}

        {/* Phase filters */}
        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5">PHASE</div>
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {['all', ...phaseOptions].map((p) => (
            <span
              key={p}
              onClick={() => onTogglePhaseFilter(p)}
              className={`${chipBase} ${isMultiFilterSelected(phaseFilter, p) ? 'bg-tracker-accent border-tracker-accent text-white font-semibold' : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
            >
              {p === 'all' ? 'All' : p}
            </span>
          ))}
        </div>

        {/* Trial status (overall_status) */}
        <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5">TRIAL STATUS</div>
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {TRIAL_STATUS_FILTER_OPTIONS.map((s) => (
            <span
              key={s.key}
              onClick={() => onToggleStatusFilter(s.key)}
              className={`${chipBase} ${isMultiFilterSelected(statusFilter, s.key) ? 'bg-tracker-accent border-tracker-accent text-white font-semibold' : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
            >
              {s.label}
            </span>
          ))}
        </div>

        {/* Source filters */}
        {sourceOptions.length > 0 && (
          <>
            <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-2 px-0.5">SOURCE</div>
            <div className="flex flex-wrap gap-1.5">
              {['all', ...sourceOptions].map((s) => (
                <span
                  key={s}
                  onClick={() => onToggleSourceFilter(s)}
                  className={`${chipBase} ${isMultiFilterSelected(sourceFilter, s) ? 'bg-tracker-accent border-tracker-accent text-white font-semibold' : 'bg-white border-tracker-line text-tracker-ink hover:border-tracker-cardBorderUnread'}`}
                >
                  {s === 'all' ? 'All' : s}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Alert settings */}
      <div className="flex-none p-3 border-t border-tracker-line relative">
        {alertPopoverOpen && (
          <div className="absolute left-3 right-3 bottom-14 z-20 bg-white border border-tracker-line rounded-xl p-3.5 shadow-lg">
            <div className="flex items-center mb-2">
              <span className="text-[10.5px] font-bold tracking-wide text-tracker-muted">ALERT ON</span>
              <button onClick={() => setAlertPopoverOpen(false)} className="ml-auto text-tracker-muted hover:text-tracker-ink text-sm leading-none">×</button>
            </div>
            <div className="flex flex-col gap-1 mb-3">
              {TRIAL_TRACKER_EVENT_TYPES.map((t) => {
                const on = !alertTypesOff[t];
                return (
                  <div
                    key={t}
                    onClick={() => onUpdateSettings({ ...alertTypesOff, [t]: on }, deliveryMode)}
                    className="flex items-center gap-2 py-1 cursor-pointer"
                  >
                    <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[9px] text-white flex-none ${on ? 'bg-tracker-accent border-tracker-accent' : 'border-tracker-line2 bg-white'}`}>
                      {on ? '✓' : ''}
                    </span>
                    <span className="text-[11.5px] text-tracker-ink">{TYPE_META[t as TrialTrackerEventType].label}</span>
                  </div>
                );
              })}
            </div>
            <div className="text-[10.5px] font-bold tracking-wide text-tracker-muted mb-1.5">DELIVERY</div>
            <div className="flex bg-tracker-line3 border border-tracker-line rounded-full p-0.5">
              {(['Instant', 'Daily', 'Weekly'] as const).map((d) => (
                <div
                  key={d}
                  onClick={() => onUpdateSettings(alertTypesOff, d)}
                  className={`flex-1 text-center py-1 text-[11px] font-semibold rounded-full cursor-pointer ${deliveryMode === d ? 'bg-white shadow-sm text-tracker-accent' : 'text-tracker-muted'}`}
                >
                  {d}
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          onClick={() => setAlertPopoverOpen((v) => !v)}
          className="flex items-center gap-2 bg-white border border-tracker-line rounded-lg px-2.5 py-2 cursor-pointer hover:border-tracker-cardBorderUnread"
        >
          <Settings size={13} className="text-tracker-muted flex-none" />
          <span className="text-[11.5px] font-semibold text-tracker-ink">Alert settings</span>
          <span className="ml-auto text-[10.5px] text-tracker-muted whitespace-nowrap">
            {deliveryMode}{mutedCount ? ` · ${mutedCount} muted` : ''}
          </span>
        </div>
        {isSuperuser && (
          <div className="mt-2">
            <button
              onClick={onRefreshNow}
              disabled={refreshing}
              title="Admin only: re-check a batch of tracked trials against ClinicalTrials.gov right now"
              className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold text-tracker-muted border border-dashed border-tracker-line2 rounded-lg py-1.5 hover:border-tracker-accent hover:text-tracker-accent disabled:opacity-50"
            >
              <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh now (admin)'}
            </button>
            {refreshResult && (
              <div className={`text-[10px] mt-1 text-center ${refreshResult.error ? 'text-rose-500' : 'text-tracker-muted'}`}>
                {refreshResult.error || `Checked ${refreshResult.checked}, found ${refreshResult.events_created} change${refreshResult.events_created === 1 ? '' : 's'}`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackerSidebar;
